import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import NameInputBox from 'components/common/NameInputBox';
import CustomInput from 'components/common/CustomInput/CustomInput';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';
import UserLookupConfirmDialog from 'components/admin/UserLookupConfirmDialog';
import {
  addSecondaryContact,
  updateSecondaryContact,
  lookupSecondaryCandidateByEmail,
} from 'api/userApi';

const EMPTY = { firstName: '', lastName: '', email: '' };

// Add or edit the family's secondary contact (personal-portal). Drives the EXISTING admin endpoints
// (the active primary is already authorised). On ADD, the typed email is preflighted: if it already
// belongs to a SplitFast account we confirm "add this existing person?" (and warn if the name differs)
// rather than silently attaching the wrong account — mirroring the admin add-secondary flow.
//   • add  → secondary is added to each of the primary's clubs (server inherits the role per club).
//   • edit → name/email are platform-level, so a single call updates them.
const SecondaryContactEditModal = ({
  isOpen,
  mode,
  primaryUserId,
  secondary,
  clubIds,
  onClose,
  onSaved,
}) => {
  const isEdit = mode === 'edit';
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  // Existing-account preflight (add path): the typed email may already belong to an account.
  const [lookupResult, setLookupResult] = useState(null);
  const [showLookup, setShowLookup] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setValues(
        isEdit && secondary
          ? {
              firstName: secondary.firstName || '',
              lastName: secondary.lastName || '',
              email: secondary.email || '',
            }
          : EMPTY
      );
      setErrors({});
      setLookupResult(null);
      setShowLookup(false);
    }
  }, [isOpen, isEdit, secondary]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const close = () => {
    setValues(EMPTY);
    setErrors({});
    setLookupResult(null);
    setShowLookup(false);
    onClose();
  };

  const validate = () => {
    const next = {};
    if (!values.firstName.trim()) next.firstName = 'First name is required';
    if (!values.lastName.trim()) next.lastName = 'Last name is required';
    if (!values.email.trim()) next.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(values.email))
      next.email = 'Email address is invalid';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const trimmed = () => ({
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    email: values.email.trim(),
  });

  // Add the secondary to each of the primary's clubs (sequential). `payload` carries either a
  // secondaryUserId (attach a confirmed existing account) or email+name+createUserIfNotExists (new one).
  const addAcrossClubs = async (payload) => {
    for (const clubId of clubIds) {
      await addSecondaryContact(clubId, primaryUserId, payload);
    }
  };

  const finishAdd = async (payload) => {
    setSaving(true);
    try {
      await addAcrossClubs(payload);
      toast.success('Secondary contact added.');
      onSaved();
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
          e.response?.data ||
          'Could not save the secondary contact.'
      );
      setSaving(false);
    }
  };

  const submit = async () => {
    if (!validate()) return;
    const { firstName, lastName, email } = trimmed();

    if (isEdit) {
      setSaving(true);
      try {
        await updateSecondaryContact(clubIds[0], primaryUserId, {
          userID: secondary.userId,
          firstName,
          lastName,
          email,
        });
        toast.success('Secondary contact updated.');
        onSaved();
      } catch (e) {
        toast.error(
          e.response?.data?.message ||
            e.response?.data ||
            'Could not save the secondary contact.'
        );
        setSaving(false);
      }
      return;
    }

    // ADD: preflight the typed email — it may already belong to an account. If so, confirm before
    // attaching (rather than silently attaching whoever owns that email under their on-record name).
    setSaving(true);
    try {
      const res = await lookupSecondaryCandidateByEmail(
        clubIds[0],
        primaryUserId,
        email
      );
      if (res?.data?.exists) {
        setLookupResult(res.data);
        setShowLookup(true);
        setSaving(false); // hand off to the lookup confirm dialog
        return;
      }
    } catch (e) {
      // Don't silently fall through to a create that could attach an existing account — make the
      // user retry so the preflight protection isn't bypassed by a transient lookup failure.
      console.log('[SecondaryContactEditModal] email preflight failed: ', e);
      toast.error('Could not verify the email address. Please try again.');
      setSaving(false);
      return;
    }

    // No existing account with that email → create a new one.
    await finishAdd({ email, firstName, lastName, createUserIfNotExists: true });
  };

  // Confirmed attaching the existing account that matched the typed email — attach by id.
  const handleLookupConfirm = async () => {
    setShowLookup(false);
    const { firstName, lastName, email } = trimmed();
    await finishAdd({
      secondaryUserId: lookupResult?.userID,
      email,
      firstName,
      lastName,
      createUserIfNotExists: false,
    });
  };

  const handleLookupCancel = () => {
    setShowLookup(false);
    setLookupResult(null);
    // Keep the form open so the user can correct the email.
  };

  return (
    <>
      <CustomNewConfirm
        isOpen={isOpen}
        title={isEdit ? 'Edit secondary contact' : 'Add secondary contact'}
        confirmButtonText={saving ? 'Saving…' : 'Save'}
        confirmButtonVariant="primary"
        cancelButtonText="Cancel"
        isLoading={saving}
        onConfirm={submit}
        onClose={close}
      >
        <div>
          <NameInputBox
            label="Name"
            formValues={values}
            errors={errors}
            handleInputChange={handleChange}
            style={{ marginBottom: 16 }}
          />
          <CustomInput
            label="Email Address"
            name="email"
            type="email"
            value={values.email}
            error={errors.email}
            onChange={handleChange}
          />
        </div>
      </CustomNewConfirm>

      <UserLookupConfirmDialog
        show={showLookup}
        lookupResult={lookupResult}
        purpose="secondary"
        context={{
          primaryContactId: primaryUserId,
          typedFirstName: values.firstName.trim(),
          typedLastName: values.lastName.trim(),
        }}
        loading={saving}
        onConfirm={handleLookupConfirm}
        onCancel={handleLookupCancel}
      />
    </>
  );
};

SecondaryContactEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['add', 'edit']).isRequired,
  primaryUserId: PropTypes.string,
  secondary: PropTypes.object, // { userId, firstName, lastName, email } in edit mode
  clubIds: PropTypes.arrayOf(PropTypes.string),
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default SecondaryContactEditModal;
